import type {
  DocPackCatalog,
  DocPackCharRange,
  DocPackPage,
  DocPackPageBlock,
  DocPackRect,
  DocPackReadingClass,
} from '@listening/doc-pack';
import { DOC_PACK_READING_CLASS_DEFAULTS } from '@listening/doc-pack';
import { docPackRectsForWord } from './overlay';
import {
  type DocPackReadingClassOverrides,
  isDocPackClassEnabled,
} from './reading-plan';

export const docPackReadingLabelColors = {
  body: '#3b82f6',
  heading: '#8b5cf6',
  citation: '#f59e0b',
  'cross-reference': '#84cc16',
  caption: '#06b6d4',
  footnote: '#f97316',
  endnote: '#d946ef',
  equation: '#14b8a6',
  table: '#22c55e',
  'header-footer': '#ef4444',
  'page-number': '#ec4899',
  artifact: '#6b7280',
  'reference-entry': '#a16207',
} as const satisfies Record<DocPackReadingClass, string>;

const docPackReadingLabelMessageKeys = {
  body: 'reading_class_body',
  heading: 'reading_class_heading',
  citation: 'reading_class_citation',
  'cross-reference': 'reading_class_cross_reference',
  caption: 'reading_class_caption',
  footnote: 'reading_class_footnote',
  endnote: 'reading_class_endnote',
  equation: 'reading_class_equation',
  table: 'reading_class_table',
  'header-footer': 'reading_class_header_footer',
  'page-number': 'reading_class_page_number',
  artifact: 'reading_class_artifact',
  'reference-entry': 'reading_class_reference_entry',
} as const satisfies Record<DocPackReadingClass, string>;

type DocPackReadingLabelMessages = Record<
  (typeof docPackReadingLabelMessageKeys)[DocPackReadingClass],
  () => string
>;

/** A label's display name from the calling app's compiled Paraglide messages. */
export const docPackReadingLabelName = (
  messages: DocPackReadingLabelMessages,
  cls: DocPackReadingClass,
) => messages[docPackReadingLabelMessageKeys[cls]]();

const docPackReadingLabelOrder = [
  'body',
  'heading',
  'header-footer',
  'page-number',
  'footnote',
  'endnote',
  'citation',
  'cross-reference',
  'caption',
  'table',
  'equation',
  'reference-entry',
  'artifact',
] as const satisfies readonly DocPackReadingClass[];

export type DocPackLabelRegion = {
  readonly id: string;
  readonly class: DocPackReadingClass;
  readonly rect: DocPackRect;
  readonly read: boolean;
};

const lineRects = (page: DocPackPage, chars: DocPackCharRange) => {
  const lines: DocPackRect[] = [];
  const rects = page.words
    .filter((word) => word.chars[0] >= chars[0] && word.chars[1] <= chars[1])
    .flatMap((word) => docPackRectsForWord(page, word));
  for (const rect of rects) {
    const line = lines.at(-1);
    const middle = (rect[1] + rect[3]) / 2;
    if (line === undefined || middle < line[1] || middle > line[3]) {
      lines.push(rect);
    } else {
      lines[lines.length - 1] = [
        Math.min(line[0], rect[0]),
        Math.min(line[1], rect[1]),
        Math.max(line[2], rect[2]),
        Math.max(line[3], rect[3]),
      ];
    }
  }
  return lines;
};

export type DocPackInlineLabelRegion = DocPackLabelRegion & {
  readonly blockId: string;
};

const blockInlineRegions = (
  page: DocPackPage,
  block: DocPackPageBlock,
  classOverrides: DocPackReadingClassOverrides,
): readonly DocPackInlineLabelRegion[] =>
  page.segments
    .filter(
      (segment) =>
        segment.blockId === block.id && segment.class !== block.class,
    )
    .flatMap((segment) =>
      lineRects(page, segment.chars).map((rect, line) => ({
        id: `${block.id}:${segment.chars[0].toString()}:${line.toString()}`,
        blockId: block.id,
        class: segment.class,
        rect,
        read: isDocPackClassEnabled(segment, classOverrides),
      })),
    );

/** One region per line of each inline segment (a citation inside a body
 * paragraph) whose class differs from its block's. */
export const docPackInlineLabelRegions = (
  page: DocPackPage,
  classOverrides: DocPackReadingClassOverrides,
) =>
  page.blocks.flatMap((block) =>
    blockInlineRegions(page, block, classOverrides),
  );

/** One region per block, followed by the block's inline regions. */
export function docPackLabelRegions(
  page: DocPackPage,
  classOverrides: DocPackReadingClassOverrides,
): readonly DocPackLabelRegion[] {
  return page.blocks.flatMap<DocPackLabelRegion>((block) => {
    const inlineRegions = blockInlineRegions(page, block, classOverrides);
    if (block.bbox === null) return inlineRegions;
    const segments = page.segments.filter(
      (candidate) => candidate.blockId === block.id,
    );
    const blockSegment =
      segments.find((segment) => segment.class === block.class) ?? segments[0];
    const read = blockSegment
      ? isDocPackClassEnabled(blockSegment, classOverrides)
      : (classOverrides[block.class] ??
        DOC_PACK_READING_CLASS_DEFAULTS[block.class]);
    return [
      { id: block.id, class: block.class, rect: block.bbox, read },
      ...inlineRegions,
    ];
  });
}

export type DocPackReadingLabel = {
  readonly class: DocPackReadingClass;
  readonly color: string;
  readonly enabled: boolean;
  readonly overridden: boolean;
};

/** The labels present in the document, in display order. A label without an
 * override counts as read when any of its text is read by default. */
export function docPackReadingLabels(
  catalog: Pick<DocPackCatalog, 'pages'>,
  classOverrides: DocPackReadingClassOverrides,
): readonly DocPackReadingLabel[] {
  return docPackReadingLabelOrder.flatMap((cls) => {
    const counts = catalog.pages.flatMap((page) => page.classes[cls] ?? []);
    if (counts.length === 0) return [];
    const override = classOverrides[cls];
    return [
      {
        class: cls,
        color: docPackReadingLabelColors[cls],
        enabled: override ?? counts.some((count) => count.chars > 0),
        overridden: override !== undefined,
      },
    ];
  });
}
